import { useState, MouseEvent, useMemo } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSnackBarMessage } from '@/components/ZonesManagementRevamped/utils';
import { abyssColors } from '@/theme/colors';
import {
  useDeleteWorkflowStageMutation,
  WorkflowStagesAndZonesDocument,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSetAtom } from 'jotai';
import * as state from '@/state';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import {
  stageDialogStyles,
  DialogTitleStyle,
  DialogActionsStyle,
  DialogActionButtonStyle,
} from './styles';

interface DeleteStageButtonProps {
  stageId: string;
  stageName?: string;
  hasZones?: boolean;
}

export const StageMenu = ({ stageId, stageName, hasZones = false }: DeleteStageButtonProps) => {
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const structureId = useGetStructureId();
  const setWorkflowStages = useSetAtom(state.workflowStages);
  const setZoneStatusCounts = useSetAtom(state.zoneStatusCounts);

  const [mutate] = useDeleteWorkflowStageMutation({
    variables: {
      input: {
        workflowStageId: stageId,
        structureId,
      },
    },
    refetchQueries: [WorkflowStagesAndZonesDocument],
    onCompleted: () => {
      setWorkflowStages((current) => current.filter((stage) => stage.id !== stageId));

      setZoneStatusCounts((current) => {
        if (!stageName) return current;
        return current.filter((status) => status.status !== stageName);
      });
    },
  });

  const open = Boolean(anchorElement);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    if (hasZones || !stageId || !structureId) return;
    try {
      await mutate();
      showSnackBar('The stage has been deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting stage:', error);
      showSnackBar(
        'There was an error deleting the stage. Please try again or contact support.',
        'error'
      );
    }
  };

  const closeDeleteDialog = () => setDeleteDialogOpen(false);
  const openDeleteDialog = () => {
    handleClose();
    setDeleteDialogOpen(true);
  };

  const { dialogTitle, dialogDescription } = useMemo(() => {
    if (hasZones) {
      return {
        dialogTitle: 'Cannot delete stage',
        dialogDescription:
          'You cannot delete this stage because it contains in-progress tasks. Please move these tasks before deleting the stage.',
      };
    }

    return {
      dialogTitle: 'Delete stage?',
      dialogDescription: `Are you sure you want to delete the ${stageName} stage from the workflow?`,
    };
  }, [hasZones, stageName]);

  return (
    <>
      <IconButton
        aria-label="more"
        id="stage-menu-button"
        aria-controls={open ? 'stage-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        disableRipple
        sx={{ padding: 0 }}
      >
        <MoreVertIcon
          sx={{
            margin: 0,
            fontSize: '2.4rem',
            color: abyssColors.primary[500],
          }}
        />
      </IconButton>
      <Menu id="stage-menu" anchorEl={anchorElement} open={open} onClose={handleClose}>
        <Tooltip
          title={hasZones ? 'Remove all zones from this stage before deleting' : ''}
          placement="right"
        >
          <Box>
            <MenuItem
              onClick={openDeleteDialog}
              disabled={hasZones}
              sx={{
                opacity: hasZones ? 0.5 : 1,
                pointerEvents: hasZones ? 'none' : 'auto',
                color: abyssColors.primary[500],
                fontWeight: 400,
                fontSize: '1.2rem',
                width: '20rem',
                padding: 1,
              }}
            >
              Delete
            </MenuItem>
          </Box>
        </Tooltip>
      </Menu>
      <GenericDialog
        open={deleteDialogOpen}
        dialogStyles={stageDialogStyles}
        paperStyles={{ maxWidth: '52.6rem' }}
        titleStyles={DialogTitleStyle}
        onClose={closeDeleteDialog}
        title={dialogTitle}
        showCloseIcon={!hasZones}
        content={
          <Box gap="1.6rem">
            <Typography
              fontSize="1.4rem"
              color={abyssColors.primary[700]}
              lineHeight={1.43}
              letterSpacing="normal"
            >
              {dialogDescription}
            </Typography>
          </Box>
        }
        actions={
          <Stack gap="1.15rem" direction="row" sx={DialogActionButtonStyle}>
            {!hasZones && (
              <Button onClick={closeDeleteDialog} sx={{ textButtonStyle }}>
                Cancel
              </Button>
            )}
            <Button onClick={handleDelete} variant="contained" sx={{ containedButtonStyle }}>
              {hasZones ? 'Ok, I understand' : 'Yes, continue'}
            </Button>
          </Stack>
        }
        actionsStyles={DialogActionsStyle}
      />
    </>
  );
};
